// pages/UserAdmin/crm/Topo.js — o topo da tela de Contatos (out/26).
//
// Do Claude Design "CRM Moderno.dc.html": busca + "Pasta" + as ações da
// lista numa pílula só + "Novo contato" na mesma linha; os filtros de
// interesse com contagem; a barra de temperatura (quanto da base está em
// cada nível — o retrato do funil sem abrir relatório) e a linha de
// Pastas / Etiquetas / Disparos agendados. Antes o título "Contatos" ocupava
// uma linha inteira que a TopBar já dizia, e a busca outra.
//
// Só apresentação: estado e handlers vêm do Contacts.js.

import { Box } from '@mui/material';
import {
  FiDownload, FiFolderPlus, FiPlus, FiRefreshCw, FiSearch, FiSliders, FiUpload, FiUserX, FiX,
  FiAlertTriangle, FiAward, FiCheck, FiClock, FiChevronDown, FiFolder, FiTag, FiTarget,
} from 'react-icons/fi';
import { STATUS as QUALIF } from '../../../components/qualificacao/regras';
import { CRM_THEME, INTEREST_LABELS, INTEREST_ORDER, OUTFIT, UNSET, folderColor, labelColor } from './theme';

const fmt = (n) => Number(n || 0).toLocaleString('pt-BR');

const foco = { '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 } };
const botao = { border: 0, cursor: 'pointer', fontFamily: OUTFIT, whiteSpace: 'nowrap', ...foco };

// Rótulo que some abaixo de 1240px: as três ações da pílula viram só ícone.
const rotuloLargo = { '@media (max-width: 1239.98px)': { display: 'none' } };

function BotaoPilula({ icone, children, titulo, onClick, disabled }) {
  return (
    <Box component="button" type="button" onClick={onClick} disabled={disabled} title={titulo} aria-label={titulo}
      sx={{
        ...botao, height: 32, minWidth: 32, px: children ? '12px' : 0, justifyContent: 'center',
        borderRadius: '999px', bgcolor: 'transparent', color: 'var(--ch-text2)',
        display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: 13.5, fontWeight: 500,
        transition: 'background-color .15s, color .15s',
        '&:hover': { bgcolor: 'var(--ch-inset)', color: 'var(--ch-text)' },
        '&:disabled': { cursor: 'default', opacity: 0.6 },
        '@media (max-width: 1239.98px)': { px: 0, width: 32 },
      }}>
      {icone}
      {children && <Box component="span" sx={rotuloLargo}>{children}</Box>}
    </Box>
  );
}

const Separador = () => <Box component="span" aria-hidden sx={{ width: '1px', height: 18, bgcolor: 'var(--ch-bdr)', flexShrink: 0 }} />;

export function BarraFerramentas({
  q, onBusca, isSupervisor, recarregando,
  onNovaPasta, onCampos, onRecarregar, onImportar, onExportar, onNovo,
}) {
  return (
    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
      <Box component="label" sx={{
        flex: '1 1 260px', minWidth: 0, height: 40, display: 'flex', alignItems: 'center', gap: '10px', px: '14px',
        borderRadius: '12px', bgcolor: 'var(--ch-card)', border: '1px solid var(--ch-bdr)', boxShadow: 'var(--cx-shadow)',
        cursor: 'text', transition: 'border-color .15s, box-shadow .15s',
        '&:focus-within': { borderColor: 'var(--ch-green-b)', boxShadow: '0 0 0 3px var(--cx-glow)' },
      }}>
        <Box component="span" sx={{ display: 'flex', color: 'var(--cx-cap)', flexShrink: 0 }}><FiSearch size={16} /></Box>
        <Box component="input" value={q} onChange={(e) => onBusca(e.target.value)} placeholder="Buscar por nome ou número..."
          aria-label="Buscar contatos"
          sx={{
            flex: 1, minWidth: 0, height: '100%', border: 0, outline: 0, bgcolor: 'transparent', p: 0,
            fontFamily: OUTFIT, fontSize: 14.5, color: 'var(--ch-text)',
            '&::placeholder': { color: 'var(--cx-cap)', opacity: 1 },
          }} />
        {q && (
          <Box component="button" type="button" onClick={() => onBusca('')} aria-label="Limpar busca"
            sx={{ ...botao, width: 28, height: 28, p: 0, borderRadius: '999px', bgcolor: 'var(--ch-inset)', color: 'var(--ch-text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <FiX size={13} strokeWidth={2.4} />
          </Box>
        )}
      </Box>

      <Box component="button" type="button" onClick={onNovaPasta} title="Criar uma pasta"
        sx={{
          ...botao, flexShrink: 0, height: 40, px: '16px', borderRadius: '12px', border: '1px solid var(--ch-green-b)',
          bgcolor: 'var(--ch-green-bg)', color: 'var(--ch-green-d)', display: 'inline-flex', alignItems: 'center', gap: '8px',
          fontSize: 14, fontWeight: 600, transition: 'border-color .15s', '&:hover': { borderColor: 'var(--ch-green)' },
        }}>
        <FiFolderPlus size={15} />Pasta
      </Box>

      <Box role="toolbar" aria-label="Ações da lista" sx={{
        display: 'flex', alignItems: 'center', gap: '2px', height: 40, p: '3px', borderRadius: '999px',
        bgcolor: 'var(--ch-card)', border: '1px solid var(--ch-bdr)', boxShadow: 'var(--cx-shadow)', flexShrink: 0,
      }}>
        {/* Só supervisor define os campos e importa: o backend exige o mesmo
            (`_is_supervisor_or_admin`); esconder é conveniência. */}
        {isSupervisor && (
          <>
            <BotaoPilula icone={<FiSliders size={15} />} titulo="Campos personalizados" onClick={onCampos}>Campos personalizados</BotaoPilula>
            <Separador />
          </>
        )}
        {/* Recarregar: a tela atualiza na hora (otimista) em vários pontos;
            quando algo falha no servidor, é o caminho curto pra ver a
            verdade sem perder filtro e busca. */}
        <BotaoPilula
          icone={<Box component="span" sx={{ display: 'flex', animation: recarregando ? 'cxGira .9s linear infinite' : 'none', '@keyframes cxGira': { to: { transform: 'rotate(360deg)' } }, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}><FiRefreshCw size={15} /></Box>}
          titulo="Recarregar a lista" onClick={onRecarregar} disabled={recarregando} />
        <Separador />
        {/* Importar antes de exportar: o fluxo que a tela ensina é "exporta →
            completa no Excel → importa de volta". */}
        {isSupervisor && (
          <BotaoPilula icone={<FiUpload size={15} />} titulo="Importar contatos de uma planilha (.xlsx ou .csv)" onClick={onImportar}>Importar</BotaoPilula>
        )}
        <BotaoPilula icone={<FiDownload size={15} />} titulo="Exportar CSV" onClick={onExportar}>Exportar CSV</BotaoPilula>
      </Box>

      <Box component="button" type="button" onClick={onNovo}
        sx={{
          ...botao, flexShrink: 0, height: 40, pl: '15px', pr: '18px', borderRadius: '999px', bgcolor: 'var(--ch-green)',
          color: 'var(--cx-on-green)', display: 'inline-flex', alignItems: 'center', gap: '7px', fontSize: 14, fontWeight: 600,
          boxShadow: '0 1px 2px var(--ch-green-glow), 0 10px 24px -10px var(--ch-green)',
          transition: 'background-color .15s, box-shadow .18s, transform .18s',
          '&:hover': { bgcolor: 'var(--ch-green-hover)', transform: 'translateY(-1px)', boxShadow: '0 1px 2px var(--ch-green-glow), 0 14px 30px -10px var(--ch-green)' },
          '&:active': { transform: 'translateY(0)' },
          '@media (prefers-reduced-motion: reduce)': { transition: 'background-color .15s', '&:hover': { transform: 'none' } },
        }}>
        <FiPlus size={16} strokeWidth={2.6} />Novo contato
      </Box>
    </Box>
  );
}

// ─── Filtros de interesse ──────────────────────────────────────────────
// Dois EIXOS, separados pelo divisor: temperatura (exclusivo entre si) e
// "Sem responsável" (combina com qualquer temperatura — "quais leads
// quentes ainda estão sem dono?"). "Não classificado" vem primeiro porque é
// fila de trabalho.

function ChipFiltro({ ativo, onClick, ponto, tracejado, cores, contagem, icone, children }) {
  const on = ativo;
  return (
    <Box component="button" type="button" onClick={onClick} aria-pressed={on}
      sx={{
        ...botao, flexShrink: 0, height: 32, display: 'inline-flex', alignItems: 'center', gap: '7px',
        pl: icone ? '11px' : '12px', pr: '6px', borderRadius: '999px', fontSize: 13.5, fontWeight: on ? 600 : 500,
        bgcolor: on ? cores.bg : 'var(--ch-card)', color: on ? cores.color : 'var(--ch-text2)',
        border: `1px ${tracejado ? 'dashed' : 'solid'} ${on ? cores.border : 'var(--ch-bdr)'}`,
        transition: 'background-color .15s, border-color .15s, color .15s',
        '&:hover': { borderColor: on ? cores.border : 'var(--ch-bdr2)' },
      }}>
      {icone}
      {(ponto || tracejado) && (
        <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: tracejado ? 'transparent' : ponto, border: tracejado ? '1.5px dashed var(--cx-cap)' : 0 }} />
      )}
      <span>{children}</span>
      <Box component="span" sx={{
        minWidth: 22, height: 20, px: '6px', borderRadius: '999px', bgcolor: on ? 'var(--ch-card)' : 'var(--ch-inset)',
        fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>{fmt(contagem)}</Box>
    </Box>
  );
}

const NIVEIS = [UNSET, ...INTEREST_ORDER];

export function FiltrosInteresse({ filtro, onFiltro, totalAll, counts, semResp, onSemResp, unassigned }) {
  const I = CRM_THEME.interest;
  return (
    <Box role="group" aria-label="Filtrar por interesse" sx={{
      flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px', mt: '14px', py: '2px',
      overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
    }}>
      <ChipFiltro ativo={filtro === 'ALL'} onClick={() => onFiltro('ALL')} ponto="var(--ch-green)" contagem={totalAll}
        cores={{ bg: 'var(--ch-green-bg)', color: 'var(--ch-green-d)', border: 'var(--ch-green-b)' }}>Todos</ChipFiltro>
      {NIVEIS.map((k) => (
        <ChipFiltro key={k} ativo={filtro === k} onClick={() => onFiltro(k)} contagem={counts[k] || 0}
          ponto={I[k].dot} tracejado={I[k].dashed} cores={I[k].ativo || I[k]}>
          {INTEREST_LABELS[k]}
        </ChipFiltro>
      ))}
      <Box component="span" aria-hidden sx={{ flexShrink: 0, width: '1px', height: 22, bgcolor: 'var(--ch-bdr2)', mx: '4px' }} />
      <ChipFiltro ativo={semResp} onClick={onSemResp} contagem={unassigned} icone={<FiUserX size={14} />}
        cores={{ bg: 'var(--cx-orange-bg)', color: 'var(--cx-orange-d)', border: 'var(--cx-orange-b)' }}>Sem responsável</ChipFiltro>
    </Box>
  );
}

// A barra de temperatura: um segmento por nível, largura = quantos. Com um
// nível filtrado, os outros apagam — a barra mostra onde a lista está.
export function BarraTemperatura({ filtro, counts }) {
  const total = NIVEIS.reduce((s, k) => s + (counts[k] || 0), 0);
  if (!total) return null;
  const I = CRM_THEME.interest;
  const resumo = NIVEIS.map((k) => `${INTEREST_LABELS[k]} ${fmt(counts[k] || 0)}`).join(', ');
  return (
    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '14px', mt: '10px' }}>
      <Box role="img" aria-label={`Distribuição por interesse: ${resumo}`} sx={{ flex: 1, minWidth: 120, display: 'flex', gap: '3px', height: 5 }}>
        {NIVEIS.filter((k) => counts[k] > 0).map((k) => (
          <Box key={k} component="span" title={`${INTEREST_LABELS[k]}: ${fmt(counts[k])}`} sx={{
            flex: `${counts[k]} 1 0`, minWidth: 4, borderRadius: '999px', background: I[k].barra || I[k].dot,
            opacity: filtro === 'ALL' || filtro === k ? 1 : 0.28, transition: 'opacity .2s',
          }} />
        ))}
      </Box>
      <Box component="span" sx={{ flexShrink: 0, fontSize: 13, color: 'var(--cx-cap)', whiteSpace: 'nowrap' }}>
        <Box component="b" sx={{ fontWeight: 600, color: 'var(--ch-text)', fontVariantNumeric: 'tabular-nums' }}>{fmt(counts[UNSET])}</Box> sem classificação
      </Box>
    </Box>
  );
}

// ─── Pastas / Etiquetas / Disparos agendados ───────────────────────────
// Chip compacto com ▾ que abre o menu; o estado do filtro vive dentro do
// chip (nome da pasta, ou "Etiquetas · 3") e o × ao lado limpa sem abrir.

function ChipMenu({ ativo, tom, icone, onClick, children, rotuloLimpar, onLimpar, semSeta, pressionado }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
      <Box component="button" type="button" onClick={onClick} aria-haspopup={semSeta ? undefined : 'menu'}
        aria-pressed={pressionado}
        sx={{
          ...botao, height: 30, display: 'inline-flex', alignItems: 'center', gap: '6px', px: '11px', borderRadius: '999px',
          fontSize: 13, fontWeight: 500, color: 'var(--ch-text2)',
          border: `1px solid ${ativo ? tom.border : 'var(--ch-bdr)'}`, bgcolor: ativo ? tom.bg : 'transparent',
          transition: 'border-color .15s', '&:hover': { borderColor: ativo ? tom.border : 'var(--ch-bdr2)' },
        }}>
        <Box component="span" sx={{ display: 'flex', color: ativo ? tom.color : 'var(--cx-cap)' }}>{icone}</Box>
        {children}
        {!semSeta && <Box component="span" sx={{ display: 'flex', opacity: 0.6 }}><FiChevronDown size={13} /></Box>}
      </Box>
      {ativo && onLimpar && (
        <Box component="button" type="button" onClick={onLimpar} title={rotuloLimpar} aria-label={rotuloLimpar}
          sx={{ ...botao, width: 28, height: 28, p: 0, borderRadius: '999px', bgcolor: 'transparent', color: 'var(--cx-cap)', display: 'flex', alignItems: 'center', justifyContent: 'center', '&:hover': { bgcolor: 'var(--ch-inset)', color: 'var(--ch-text)' } }}>
          <FiX size={13} strokeWidth={2.4} />
        </Box>
      )}
    </Box>
  );
}

// O que o filtro de qualificação mostra (plural da lista, não da marcação).
export const ROTULO_FILTRO_QUALIF = {
  qualificado: 'Qualificados', desqualificado: 'Desqualificados', venda: 'Venda fechada', sem: 'Sem marcação',
};

export function FiltrosLinha({
  folders, labels, pastaAtiva, etiquetasAtivas, onAbrirPastas, onLimparPasta, onAbrirEtiquetas, onLimparEtiquetas, onDisparos,
  deAnuncio = 0, soAnuncio = false, onSoAnuncio,
  qualifAtiva = null, onAbrirQualif, onLimparQualif,
}) {
  const pasta = folders.find((f) => f.id === pastaAtiva);
  const lbs = labels.filter((l) => etiquetasAtivas.has(l.id));
  const tomPasta = pasta ? folderColor(pasta.color) : null;
  const tomEtq = lbs.length ? labelColor(lbs[0].color) : null;
  return (
    <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', mt: '10px' }}>
      {folders.length > 0 && (
        <ChipMenu ativo={!!pasta} tom={tomPasta} icone={<FiFolder size={14} />} onClick={onAbrirPastas}
          onLimpar={onLimparPasta} rotuloLimpar="Limpar filtro de pasta">
          {pasta ? (<><span>Pasta ·</span><Box component="span" sx={{ fontWeight: 600, color: tomPasta.color }}>{pasta.name}</Box></>) : 'Pastas'}
        </ChipMenu>
      )}
      {labels.length > 0 && (
        <ChipMenu ativo={lbs.length > 0} tom={tomEtq} icone={<FiTag size={14} />} onClick={onAbrirEtiquetas}
          onLimpar={onLimparEtiquetas} rotuloLimpar="Limpar filtro de etiquetas">
          {lbs.length === 0 ? 'Etiquetas'
            : (<><span>{lbs.length === 1 ? 'Etiqueta ·' : 'Etiquetas ·'}</span><Box component="span" sx={{ fontWeight: 600, color: 'var(--ch-text)' }}>{lbs.length === 1 ? lbs[0].name : lbs.length}</Box></>)}
        </ChipMenu>
      )}
      {/* Veio de anúncio (out/26, rastreamento PR 1): liga e desliga, como o
          "Sem responsável". Só aparece pra quem tem lead de anúncio — empresa
          que não anuncia não precisa de mais um botão. */}
      {(deAnuncio > 0 || soAnuncio) && (
        <ChipMenu ativo={soAnuncio} semSeta pressionado={soAnuncio} onClick={onSoAnuncio}
          tom={{ bg: 'var(--ch-purple-bg)', color: 'var(--ch-purple-d)', border: 'var(--ch-purple-b)' }}
          icone={<FiTarget size={14} />}>
          Veio de anúncio
          <Box component="span" sx={{
            minWidth: 22, height: 20, px: '6px', borderRadius: '999px', bgcolor: soAnuncio ? 'var(--ch-card)' : 'var(--ch-inset)',
            fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}>{fmt(deAnuncio)}</Box>
        </ChipMenu>
      )}
      {/* Qualificação (out/26, rastreamento PR 2): qualificados,
          desqualificados, venda fechada ou sem marcação. Sempre presente:
          qualquer contato pode ser marcado. */}
      <ChipMenu ativo={!!qualifAtiva} icone={<FiAward size={14} />} onClick={onAbrirQualif}
        tom={QUALIF[qualifAtiva] ? { bg: QUALIF[qualifAtiva].bg, color: QUALIF[qualifAtiva].cor, border: QUALIF[qualifAtiva].borda }
          : { bg: 'var(--ch-inset)', color: 'var(--ch-text)', border: 'var(--ch-bdr2)' }}
        onLimpar={onLimparQualif} rotuloLimpar="Limpar filtro de qualificação">
        {qualifAtiva ? (<><span>Qualificação ·</span><Box component="span" sx={{ fontWeight: 600 }}>{ROTULO_FILTRO_QUALIF[qualifAtiva]}</Box></>) : 'Qualificação'}
      </ChipMenu>
      {/* Disparos agendados (era "Lembretes" até out/26 — o nome passou pros
          retornos da Agenda). Da mesma família de Pastas e Etiquetas — o
          que fazer com um recorte de contatos —, mas abre diálogo: agendar
          pede template, número e data. Sem contador: o número muda sozinho
          quando um dispara, e contador velho é pior que nenhum. */}
      <ChipMenu ativo={false} icone={<FiClock size={14} />} onClick={onDisparos} semSeta>Disparos agendados</ChipMenu>
    </Box>
  );
}

// ─── Aviso de resultado (ação em lote, importação, erro) ───────────────
const TONS = {
  success: { bg: 'var(--ch-green-bg)', b: 'var(--ch-green-b)', c: 'var(--ch-green-d)', icone: <FiCheck size={16} strokeWidth={2.4} /> },
  warning: { bg: 'var(--ch-amber-bg)', b: 'var(--ch-amber-b)', c: 'var(--ch-amber-d)', icone: <FiAlertTriangle size={16} /> },
  error: { bg: 'var(--ch-red-bg)', b: 'var(--ch-red-b)', c: 'var(--ch-red-d)', icone: <FiAlertTriangle size={16} /> },
};

export function Aviso({ tom = 'success', onFechar, acao, children }) {
  const t = TONS[tom] || TONS.success;
  return (
    <Box role={tom === 'error' ? 'alert' : 'status'} sx={{
      flexShrink: 0, mt: '10px', display: 'flex', alignItems: 'center', gap: '10px', p: '10px 10px 10px 14px',
      borderRadius: '12px', bgcolor: t.bg, border: `1px solid ${t.b}`, color: t.c, fontSize: 14.5, fontWeight: 500,
    }}>
      <Box component="span" sx={{ display: 'flex', flexShrink: 0 }}>{t.icone}</Box>
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
      {/* Ação do aviso — hoje só o "Desfazer" da qualificação em lote. */}
      {acao && (
        <Box component="button" type="button" onClick={acao.onClick}
          sx={{ ...botao, height: 30, px: '12px', borderRadius: '9px', bgcolor: 'transparent', color: 'inherit', fontWeight: 700,
            textDecoration: 'underline', textUnderlineOffset: 3, flexShrink: 0, '&:hover': { bgcolor: 'rgba(127,127,127,.12)' } }}>
          {acao.rotulo}
        </Box>
      )}
      {onFechar && (
        <Box component="button" type="button" onClick={onFechar} aria-label="Fechar aviso"
          sx={{ ...botao, width: 28, height: 28, p: 0, borderRadius: '999px', bgcolor: 'transparent', color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <FiX size={13} strokeWidth={2.4} />
        </Box>
      )}
    </Box>
  );
}
